/**
 * The "Interner Ansprechpartner" row — a catalog picker that saves on its own
 * (D3f.2).
 *
 * WHY THIS ROW IS NOT LIKE THE OTHER FIVE. Currency, tax, bank account, payment
 * type and language ride the shared form: the page holds their value, a diff
 * collects what changed and one "Änderungen speichern" sends them together
 * through `PATCH /api/admin/bexio/config`, which never talks to Bexio. This one
 * cannot travel that path. Storing a user id also means storing the NAME beside
 * it, the browser is not allowed to author that name, and only the server can
 * resolve it — so the write has to ask Bexio, and the generic save has to stay
 * provider-free so an admin can still fix a template slug during an outage.
 *
 * Hence its own endpoint, and hence its own save control here. Two save buttons
 * on one page is a real cost; the alternative was making every settings save
 * depend on Bexio being up, which is worse.
 *
 * IT REUSES `BexioCatalogSelect` RATHER THAN REIMPLEMENTING IT. Everything that
 * makes a catalog row hard is identical here — options arrive over the network,
 * the load can fail, and the stored id may not be in the list that comes back.
 * That last case is not hypothetical on this row: production holds a
 * `target_user_id` that resolves to a 404, so the orphan entry ("ID 2 — nicht im
 * Katalog") is what the operator actually sees, and it is what tells them why
 * they are being asked to choose again. Nothing here auto-selects a replacement.
 */
import React from "react";

import BexioCatalogSelect from "./BexioCatalogSelect";

const BUTTON =
  "rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export default function BexioTargetUserRow({
  id,
  label,
  hint,
  value = null,
  options = [],
  catalogState = "idle",
  catalogError = "",
  onRetry,
  missing = false,
  dirty = false,
  saving = false,
  saveError = "",
  onChange,
  onSave,
}) {
  const errorId = `${id}-save-error`;

  return (
    <BexioCatalogSelect
      id={id}
      label={label}
      hint={hint}
      value={value}
      options={options}
      catalogState={catalogState}
      catalogError={catalogError}
      onRetry={onRetry}
      missing={missing}
      onChange={onChange}
      action={
        <>
          <button
            type="button"
            onClick={onSave}
            // Disabled until the selection actually differs from what is stored:
            // a save that sends the stored value would still spend a live Bexio
            // request, and on this row that request can fail.
            disabled={!dirty || saving}
            aria-describedby={saveError ? errorId : undefined}
            className={BUTTON}
          >
            {/* A DISTINCT in-flight name, not the header button's bare
                "Speichert …". Two buttons sharing one accessible name is a real
                cost to anyone navigating by button list, and this page now has
                two saves that do genuinely different things — one talks to
                Bexio, one deliberately never does. */}
            {saving ? "Speichert Ansprechpartner …" : "Ansprechpartner speichern"}
          </button>
          {saveError ? (
            // `role="alert"` and a text sentence, not a colour: the failure has
            // to reach someone who cannot distinguish the amber tint.
            <span
              id={errorId}
              role="alert"
              className="mt-1 block text-xs text-amber-800 dark:text-amber-300"
            >
              {saveError}
            </span>
          ) : null}
        </>
      }
    />
  );
}
